// Bildschirm "Anschriften" -- die Pflege von Adressverwendungen (ST-PTY-002,
// zweiter Schnitt; CM-PTY-006; ADR-0056).
//
// EIN EIGENER BILDSCHIRM, dieselbe Entscheidung wie bei Kontaktwegen und
// Kennungen: Kapitel 5.2 Nr. 2 verlangt EINE Hauptaktion je Bildschirm, und die
// dieses Bildschirms ist "Anschrift hinzufuegen". Er hat eine eigene Adresse
// unter /kontakte/{partyId}/adressen.
//
// "NICHT GEPRUEFT" IST DIE ZUSAGE DIESES SCHNITTS (A1 vom 28.09.2026): LIAISON
// speichert, was eingegeben wurde, und prueft weder Schreibweise noch
// Zustellbarkeit. Jede Anschrift traegt das in Worten, und EIN Satz oben sagt
// einmal, was es heisst (5.2 Nr. 14 -- keine Erklaerung an jeder Zeile). Kein
// Geocoding-Status, keine Karte, kein "verifiziert": nichts davon waere wahr.
//
// PROGRESSIVE OFFENLEGUNG (5.2 Nr. 9):
//   * Der Endgrund erscheint erst, wenn jemand "Beenden" waehlt.
//   * Die frueheren Anschriften liegen zugeklappt: Beleg fuer "beendet statt
//     ueberschrieben" (CM-PTY-006), aber nicht die Arbeit.
//   * Kennung, Sperrstand und Pruefpolicy erscheinen gar nicht.
//
// DIE PFLICHTANGABEN SIND DIE DES SERVERS (A2): Zweck, Land, eine Adresszeile,
// Ort; die Postleitzahl nur fuer Deutschland. Die Oberflaeche prueft sie NICHT
// vorab -- sie kennzeichnet sie und uebersetzt die Antwort des Servers je Feld.
// Eine zweite Pruefung hier liefe beim naechsten Laenderschnitt auseinander.
//
// SICHERE FEHLERERHOLUNG (NFR-UX-005, CM-UX-004): ein Konflikt bietet "Anschriften
// neu laden" an und ausdruecklich KEIN "erneut versuchen"; die Eingabe wird
// erst nach dem Erfolg geleert. Bricht beim Hinzufuegen die Verbindung ab,
// steht "Erneut versuchen" da -- seit ADR-0060 sicher, weil die Wiederholung
// dieselbe Kennung des Absendens traegt (client-request.ts).
//
// "ANSCHRIFT EINFUEGEN" (29.09.2026, S1): ein Textfeld oben im Formular legt
// den Vorschlag des Zerlegers (address-paste.ts) ueber DASSELBE setEingabe in
// die Felder, das auch das Tippen benutzt. Gespeichert wird erst, was die
// Nutzerin bestaetigt; der eingefuegte Text selbst geht nie an den Server (er
// steht ausserhalb von `eingabe` und damit auch ausserhalb des Fingerprints
// der Absendekennung). Angefasste Felder ueberschreibt ein weiteres Einfuegen
// nicht -- JE FELD, anders als die Schnellerfassung (siehe
// applyAddressProposal). Der Zweck wird nie vorbelegt; das Land bleibt leer,
// wenn die PLZ es nicht traegt. Was nicht uebernommen wurde, steht darunter
// in Worten -- OHNE eigene aria-live-Region; was uebernommen wurde, meldet
// die EINE Statusregion, sobald sich das Ergebnis aendert.
//
// EINE ANSCHRIFT, MEHRERE ZWECKE: die Liste zeigt je Anschrift eine Zeile,
// bei mehreren offenen Verwendungen mit "Zwecke: ...". Beendet wird je Zweck.
// "Auch verwenden als ..." ist eine zurueckhaltende Zeilenaktion; die
// Zweckwahl erscheint erst auf Anforderung und bietet nur, was fehlt (5.2
// Nr. 9). Sie legt keine zweite Anschrift an, sondern eine weitere Verwendung
// derselben (ADR-0056 Nachtrag Ziffer 3).
//
// BARRIEREFREIHEIT (NFR-ACC-001): GENAU EINE Region mit aria-live; der Fokus
// wandert nach jedem Befehl dorthin; Zustaende stehen in Worten und nie in
// Farbe allein; jeder Zeilenknopf traegt Zweck und erste Zeile im Namen.

import { useCallback, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Field,
  Input,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Select,
  Textarea,
} from '@fluentui/react-components';

import {
  ADDRESS_CHOOSE_COUNTRY,
  ADDRESS_PASTE_HINT,
  ADDRESS_PASTE_KEPT,
  ADDRESS_PASTE_LABEL,
  ADDRESS_PASTE_NONE,
  ADDRESS_PASTE_REST_HEADING,
  ADDRESS_PASTE_TOO_LONG,
  addressAnchor,
  addressFieldHint,
  addressPasteAnnouncement,
  addressPasteRest,
  applyAddressProposal,
  MAXIMUM_PASTE_LENGTH,
  NO_ADDRESS,
  parseAddressText,
  PASTED_ADDRESS_FIELDS,
  type AddressPasteResult,
  type PastedAddressField,
} from '../address-paste.ts';
import { TRIM_AND_NFC } from '../client-request.ts';
import {
  addAddress,
  addAddressUsage,
  additionalAddressNotice,
  confirmationFor,
  correctAddress,
  correctionConfirmation,
  countryName,
  countryOptions,
  DEFAULT_COUNTRY,
  endAddress,
  endReasonLabel,
  fetchAddresses,
  groupAddresses,
  HISTORY_TRUNCATED_NOTICE,
  normalizationLabel,
  pruefPilleSichtbar,
  OFFERED_ADDRESS_END_REASONS,
  OFFERED_USAGES,
  POSTAL_CODE_REQUIRED_COUNTRY,
  remainingUsages,
  SUBMIT_FAILED_MESSAGE,
  UNCHECKED_EXPLANATION,
  USAGE_SUBMIT_FAILED_MESSAGE,
  usageLabel,
  usagesHeading,
  validationMessageFor,
  type AddressGroup,
  type AddressView,
  type EndedAddressView,
  type PartyAddresses,
} from '../addresses.ts';
import { formatInstant, partyHref } from '../parties.ts';
import { formatSinceMonth, sinceText } from '../relationships.ts';
import { notWritableNotice } from '../party-lifecycle.ts';
import { Pflegeabschnitt, Pflegehistorie, Pflegeliste, usePflegeStyles, Zeilenaktionen } from '../shell/pflegeabschnitt.tsx';
import { AnlegenFormular, AnlegenOeffner, PflegeKopf, useAnlegen } from './pflege-kopf.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { AnschriftKorrigieren, type KorrekturEingabe } from './anschrift-korrigieren.tsx';
import { WeitereVerwendung } from './weitere-verwendung.tsx';
import { Lage, LageBeschreibung } from './lage.tsx';
import { useMaintainedSection } from '../shell/use-maintained-section.ts';
import { AppLink } from '../shell/router.tsx';
import { useKontaktStyles } from './kontakt/stile.ts';
import { useUeberOrganisation } from './kontakt/ueber-organisation.tsx';

const useStyles = makeStyles({
  // Das native select nimmt die Breite seiner laengsten Option; ohne Grenze ragt
  // es schmal ueber die Karte (DESIGN-P3 #579, wie "Kontakt anlegen").
  land: { width: '100%', maxWidth: '100%', minWidth: 0, '& select': { maxWidth: '100%' } },
  anschrift: {
    // `address` traegt kursiv als Browservorgabe; eine Anschrift ist hier
    // Inhalt und keine Hervorhebung.
    fontStyle: 'normal',
    display: 'block',
    marginTop: '0.25rem',
    overflowWrap: 'anywhere',
  },
  zeile: { display: 'block' },
  rest: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    overflowWrap: 'anywhere',
  },
  restliste: { margin: 0, paddingLeft: '1.25rem' },
});

export interface AdressenScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
}

/**
 * Die Anschrift als Block: die eingegebenen Zeilen, dann das Land.
 *
 * EXPORTIERT, weil das Kontaktdetail dieselbe Darstellung zeigt (Party 360,
 * zweiter Schnitt) -- EINE Stelle fuer Zeilen und Land, damit eine Anschrift
 * im Detail nie anders aussieht als auf ihrer Pflegeseite.
 */
export function Anschrift({ lines, countryCode }: { readonly lines: readonly ReactNode[]; readonly countryCode: string }): ReactElement {
  const own = useStyles();
  return (
    <address className={own.anschrift}>
      {lines.map((line, index) => (
        // Der Index ist hier der Schluessel, weil zwei gleiche Zeilen moeglich
        // und die Zeilen unveraenderlich sind.
        <span key={index} className={own.zeile}>
          {line}
        </span>
      ))}
      <span className={own.zeile}>{countryName(countryCode)}</span>
    </address>
  );
}

/**
 * Eine gueltige Anschrift mit ihren Zwecken. Beendet wird JE ZWECK -- "die
 * Rechnung geht nicht mehr dorthin" beendet nicht die Lieferung. Der
 * Endgrund ist Pflicht und erscheint erst mit der Beendigung.
 */
function Adresse({
  group,
  nurLesen,
  beendenFuer,
  verwendenOffen,
  busy,
  zuruecktreten,
  grund,
  neuerZweck,
  onGrund,
  onNeuerZweck,
  onBeendenOeffnen,
  onBeendenAbbrechen,
  onBeenden,
  onVerwendenOeffnen,
  onVerwendenAbbrechen,
  onVerwenden,
  korrektur,
}: {
  /** "Anschrift korrigieren" fuer diese Anschrift -- vom Bildschirm gebaut, weil es dessen Feldfehler traegt. */
  readonly korrektur: ReactElement;
  readonly group: AddressGroup;
  /** Der Kontakt ist nicht beschreibbar: weder Korrektur noch "Auch verwenden als". */
  readonly nurLesen: boolean;
  readonly beendenFuer: string | undefined;
  readonly verwendenOffen: boolean;
  readonly busy: boolean;
  readonly zuruecktreten: boolean;
  readonly grund: string;
  readonly neuerZweck: string;
  readonly onGrund: (code: string) => void;
  readonly onNeuerZweck: (code: string) => void;
  readonly onBeendenOeffnen: (address: AddressView) => void;
  readonly onBeendenAbbrechen: () => void;
  readonly onBeenden: (address: AddressView) => void;
  readonly onVerwendenOeffnen: () => void;
  readonly onVerwendenAbbrechen: () => void;
  readonly onVerwenden: () => void;
}): ReactElement {
  const own = usePflegeStyles();
  const mehrere = group.usages.length > 1;
  const anschrift = group.lines.join(', ');
  const weitere = remainingUsages(group);
  return (
    <li className={own.eintrag}>
      <Body1>
        <strong>{usagesHeading(group)}</strong>
      </Body1>
      <Anschrift lines={group.lines} countryCode={group.countryCode} />
      {/* Leer entfällt die Zeile ganz, sonst bliebe ein Abstand ohne Inhalt. */}
      {pruefPilleSichtbar(group.normalizationStatus) || !mehrere || group.locationStatus !== undefined ? (
        <span className={own.merkmale}>
          {/* In WORTEN und nicht als Farbe allein (NFR-ACC-001, WCAG 1.4.1). */}
          {pruefPilleSichtbar(group.normalizationStatus) ? (
            <Badge appearance="outline" color="informative">
              {normalizationLabel(group.normalizationStatus)}
            </Badge>
          ) : undefined}
          {mehrere ? undefined : <Caption1>{sinceText(formatSinceMonth(group.usages[0]?.validFrom ?? ''))}</Caption1>}
          {group.locationStatus === undefined ? undefined : <Lage status={group.locationStatus} />}
        </span>
      ) : undefined}
      {group.locationStatus === undefined ? undefined : <LageBeschreibung status={group.locationStatus} />}

      {/* Zuerst, was ergaenzt oder korrigiert; "Beenden" (aendert den Bestand) steht am Ende
          der Reihe, damit der riskante Knopf nie zuerst unter dem Finger liegt. */}
      {nurLesen ? undefined : korrektur}

      {!nurLesen && weitere.length > 0 ? (
        <WeitereVerwendung
          name={anschrift}
          frage="Wofür soll diese Anschrift zusätzlich gelten?"
          wirkung="Es entsteht keine zweite Anschrift. Jeder Zweck lässt sich später einzeln wieder aufgeben."
          optionen={weitere}
          offen={verwendenOffen}
          busy={busy}
          wert={neuerZweck}
          onWert={onNeuerZweck}
          onOeffnen={onVerwendenOeffnen}
          onAbbrechen={onVerwendenAbbrechen}
          onBestaetigen={onVerwenden}
          zuruecktreten={zuruecktreten}
        />
      ) : undefined}

      {group.usages.map((address) => {
        const zweck = usageLabel(address.usageCode);
        return (
          <div key={address.addressUsageId} className={mehrere ? undefined : own.zweckEinzeln}>
            {mehrere ? (
              <Caption1 className={own.hinweis}>
                {zweck}, {sinceText(formatSinceMonth(address.validFrom))}
              </Caption1>
            ) : undefined}
            <Zeilenaktionen
              name={`${zweck} ${anschrift}`.trim()}
              offen={beendenFuer === address.addressUsageId}
              busy={busy}
              aktion="Nicht mehr verwenden"
              frage={mehrere ? `Warum gilt der Zweck „${zweck}“ an dieser Anschrift nicht mehr?` : 'Warum wird diese Anschrift nicht mehr verwendet?'}
              wirkung={
                mehrere
                  ? 'Nur dieser Zweck fällt weg. Die übrigen gelten weiter; er bleibt unter „Frühere Anschriften“ nachlesbar.'
                  : 'Die Anschrift wird nicht gelöscht. Sie bleibt unter „Frühere Anschriften“ nachlesbar.'
              }
              bestaetigen={mehrere ? 'Für diesen Zweck nicht mehr verwenden' : 'Nicht mehr verwenden'}
              gruende={{ optionen: OFFERED_ADDRESS_END_REASONS, wert: grund, onWert: onGrund }}
              onOeffnen={() => onBeendenOeffnen(address)}
              onAbbrechen={onBeendenAbbrechen}
              onBeenden={() => onBeenden(address)}
            />
          </div>
        );
      })}

    </li>
  );
}

function FruehereAdresse({ entry }: { readonly entry: EndedAddressView }): ReactElement {
  const own = usePflegeStyles();
  return (
    <li className={own.eintrag}>
      <Body1>
        <strong>{usageLabel(entry.usageCode)}</strong>
      </Body1>
      <Anschrift lines={entry.lines} countryCode={entry.countryCode} />
      <span className={own.merkmale}>
        <Caption1>
          nicht mehr verwendet seit {formatInstant(entry.endedAt)}, {endReasonLabel(entry.endReasonCode)}
        </Caption1>
      </span>
    </li>
  );
}

const LEERE_EINGABE: Readonly<Record<'usageCode' | PastedAddressField, string>> = Object.freeze({
  usageCode: '',
  countryCode: DEFAULT_COUNTRY,
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  locality: '',
});

export function AdressenScreen({ apiBaseUrl, accessToken, onReauthenticate, partyId }: AdressenScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const pflege = usePflegeStyles();
  const [eingabe, setEingabe] = useState<typeof LEERE_EINGABE>(LEERE_EINGABE);
  const [beendenFuer, setBeendenFuer] = useState<string | undefined>(undefined);
  /** Die Anschrift, deren Zweckwahl offen ist, und der gewaehlte Zweck -- ohne Vorbelegung. */
  const [verwendenFuer, setVerwendenFuer] = useState<string | undefined>(undefined);
  const [neuerZweck, setNeuerZweck] = useState('');
  /** Die Anschrift, deren Korrektur offen ist. */
  const [korrigierenFuer, setKorrigierenFuer] = useState<string | undefined>(undefined);
  /**
   * Welches Anlegen "Erneut versuchen" wiederholt: eine neue Anschrift oder
   * "auch verwenden als" an dieser Anschrift. Nur die ART steht hier, nicht
   * die Eingabe -- wiederholt wird mit der Eingabe, die JETZT dasteht, und
   * `abschnitt.create` entscheidet daran, ob die Absendekennung bleibt.
   */
  const letzteAnlage = useRef<
    { readonly art: 'anschrift' } | { readonly art: 'zweck'; readonly addressId: string; readonly usageCode: string }
  >({ art: 'anschrift' });
  const [grund, setGrund] = useState<string>(OFFERED_ADDRESS_END_REASONS[0]?.code ?? '');
  const laender = useMemo(() => countryOptions(), []);
  /** "Anschrift einfuegen": der Text, was der Zerleger darin fand, und die Felder, die der Nutzerin gehoeren. */
  const [einfuegeText, setEinfuegeText] = useState('');
  const [einfuegen, setEinfuegen] = useState<AddressPasteResult>(NO_ADDRESS);
  const [angefasst, setAngefasst] = useState<ReadonlySet<PastedAddressField>>(() => new Set());
  /**
   * Was das Einfuegen gebracht hat, fuer die EINE Statusregion. Sie aendert
   * sich NUR, wenn sich das Ergebnis aendert (eine andere Anschrift, keine
   * mehr) -- nicht bei jedem Tastendruck im Textfeld.
   */
  const [einfuegeMeldung, setEinfuegeMeldung] = useState<string | undefined>(undefined);
  const einfuegeAnkerRef = useRef<string | undefined>(undefined);
  /** Nach dem Erfolg und nach "schon angelegt, inzwischen geaendert" -- zusammen mit der Eingabe. */
  const einfuegenLeeren = useCallback((): void => {
    setEinfuegeText('');
    setEinfuegen(NO_ADDRESS);
    setAngefasst(new Set());
    einfuegeAnkerRef.current = undefined;
  }, []);

  const abschnitt = useMaintainedSection<PartyAddresses>({
    accessToken,
    onReauthenticate,
    sectionKey: `${apiBaseUrl}|${partyId}`,
    fetchSection: (token) => fetchAddresses({ baseUrl: apiBaseUrl, accessToken: token, partyId }),
    createMessages: { submitFailed: SUBMIT_FAILED_MESSAGE },
  });
  const { busy, fieldErrors: feldfehler, submitFailed: absendenFehlgeschlagen } = abschnitt;
  // Der Lebenszyklus steht in der Antwort dieser Seite; fehlt er (aelteres Image) oder schlug ein spaeteres
  // Lesen fehl, gilt der zuletzt bekannte Stand, sonst bleibt das Angebot stehen und der Server lehnt mit seinem Satz ab.
  const nurLesen = notWritableNotice(abschnitt.data?.lifecycleStatus);
  const ueberStil = useKontaktStyles();
  // P7 A: die übernommene Geschäftsanschrift; ein Fehler zeigt nichts, die eigenen Anschriften laden unabhängig.
  const ueber = useUeberOrganisation({ apiBaseUrl, accessToken, partyId, aktiv: true, stand: abschnitt.data });
  const anlegen = useAnlegen(abschnitt.status === 'geladen' && abschnitt.data !== undefined && abschnitt.data.postalAddresses.length === 0, partyId, abschnitt.status === 'laedt');

  /** Die Eingabe nach einem Erfolg: leer, das gewaehlte Land bleibt. */
  const eingabeLeeren = (): void => {
    setEingabe((vorher) => ({ ...LEERE_EINGABE, countryCode: vorher.countryCode }));
    einfuegenLeeren();
  };

  /**
   * Hinzufuegen -- aus dem Formular und aus "Erneut versuchen". Der Server
   * vergleicht Anschriften getrimmt UND in NFC (ADR-0060 Ziffer 3); dieselbe
   * Normalisierung entscheidet, ob die Absendekennung bleibt.
   */
  const hinzufuegen = (): void => {
    letzteAnlage.current = { art: 'anschrift' };
    setKorrigierenFuer(undefined);
    // Jeder Befehl raeumt die Ansage des Einfuegens ab: sie beschreibt eine
    // Eingabe, die gleich gespeichert oder abgelehnt ist.
    setEinfuegeMeldung(undefined);
    void abschnitt.create({
      input: eingabe,
      normalization: TRIM_AND_NFC,
      run: (token, clientRequestId) =>
        addAddress({ baseUrl: apiBaseUrl, accessToken: token, partyId, input: eingabe, clientRequestId }),
      confirm: (applied) => confirmationFor('add', applied.usageCode),
      clearInput: eingabeLeeren,
      onApplied: () => {
        setBeendenFuer(undefined);
        anlegen.erledigt();
      },
    });
  };

  const verwendenSchliessen = (): void => {
    setVerwendenFuer(undefined);
    setNeuerZweck('');
  };

  const verwenden = (group: AddressGroup, usageCode: string): void => {
    const quelle = group.usages[0];
    if (quelle === undefined || usageCode === '') {
      return;
    }
    letzteAnlage.current = { art: 'zweck', addressId: group.addressId, usageCode };
    setEinfuegeMeldung(undefined);
    void abschnitt.create({
      input: { quelle: quelle.addressUsageId, usageCode },
      run: (token, clientRequestId) =>
        addAddressUsage({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          sourceAddressUsageId: quelle.addressUsageId,
          usageCode,
          clientRequestId,
        }),
      confirm: (applied) => confirmationFor('add_usage', applied.usageCode),
      clearInput: verwendenSchliessen,
      messages: { submitFailed: USAGE_SUBMIT_FAILED_MESSAGE },
    });
  };

  const korrigieren = (group: AddressGroup, eingabeKorrektur: KorrekturEingabe): void => {
    const quelle = group.usages[0];
    if (quelle === undefined) {
      return;
    }
    setEinfuegeMeldung(undefined);
    void abschnitt.command({
      run: (token) =>
        correctAddress({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          addressUsageId: quelle.addressUsageId,
          input: eingabeKorrektur,
          addressLockVersion: group.addressLockVersion,
        }),
      confirm: (applied) => correctionConfirmation(applied.changed, group.usages.length),
      onApplied: () => setKorrigierenFuer(undefined),
    });
  };

  const beenden = (address: AddressView): void => {
    setEinfuegeMeldung(undefined);
    void abschnitt.command({
      run: (token) =>
        endAddress({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          addressUsageId: address.addressUsageId,
          endReasonCode: grund,
          lockVersion: address.lockVersion,
        }),
      confirm: (applied) => confirmationFor('end', applied.usageCode),
      onApplied: () => setBeendenFuer(undefined),
    });
  };

  const fehlerFuer = (fieldPath: string): string | null => {
    // Solange eine Korrektur offen ist, gehoeren die Feldmeldungen ihr und
    // nicht dem Formular oben.
    if (korrigierenFuer !== undefined) {
      return null;
    }
    const fehler = feldfehler.find((eintrag) => eintrag.fieldPath === fieldPath);
    return fehler === undefined ? null : validationMessageFor(fieldPath, fehler.reasonCode);
  };
  const setze = (feld: keyof typeof LEERE_EINGABE) => (wert: string) => {
    // Was die Nutzerin anfasst, gehoert ihr: ein weiteres Einfuegen laesst es stehen.
    if ((PASTED_ADDRESS_FIELDS as readonly string[]).includes(feld)) {
      setAngefasst((vorher) => new Set([...vorher, feld as PastedAddressField]));
    }
    setEingabe((vorher) => ({ ...vorher, [feld]: wert }));
  };
  /**
   * Das Einfuegen aendert NUR die Eingabe -- weder die gehaltene Kennung noch
   * "Erneut versuchen" (ADR-0060 Ziffer 9): liefert es dieselbe Eingabe,
   * bleibt die Kennung, sonst entsteht beim naechsten Absenden eine neue.
   */
  const fuegeEin = (text: string): void => {
    setEinfuegeText(text);
    const erkannt = text.trim().length === 0 || text.length > MAXIMUM_PASTE_LENGTH ? NO_ADDRESS : parseAddressText(text);
    setEinfuegen(erkannt);
    setEingabe((vorher) => applyAddressProposal(vorher, erkannt.proposal, angefasst));
    const zuLang = text.length > MAXIMUM_PASTE_LENGTH;
    const anker = text.trim().length === 0 ? '' : zuLang ? 'zu-lang' : (addressAnchor(erkannt) ?? 'keine');
    if (anker !== (einfuegeAnkerRef.current ?? '')) {
      einfuegeAnkerRef.current = anker === '' ? undefined : anker;
      setEinfuegeMeldung(addressPasteAnnouncement(erkannt, text));
    }
  };
  const einfuegeRest = addressPasteRest(einfuegen);
  const einfuegeHinweis =
    einfuegeText.length > MAXIMUM_PASTE_LENGTH
      ? ADDRESS_PASTE_TOO_LONG
      : einfuegeText.trim().length > 0 && einfuegen.proposal === undefined
      ? ADDRESS_PASTE_NONE
      : einfuegen.proposal !== undefined && angefasst.size > 0
        ? `${ADDRESS_PASTE_HINT} ${ADDRESS_PASTE_KEPT}`
        : ADDRESS_PASTE_HINT;
  const plzPflicht = eingabe.countryCode === POSTAL_CODE_REQUIRED_COUNTRY;
  const gewaehlterZweck = OFFERED_USAGES.find((eintrag) => eintrag.code === eingabe.usageCode);


  return (
    <>
    <PflegeKopf partyId={partyId} name={abschnitt.data?.displayName} bereich="Anschriften" />
    <Pflegeabschnitt
      abschnitt={abschnitt}
      partyId={partyId}
      texte={{
        laden: 'Anschriften werden geladen',
        feldfehlerTitel: 'Die Anschrift konnte nicht übernommen werden.',
        feldfehlerText: 'Prüfe die markierten Angaben und versuche es erneut.',
        neuLaden: 'Anschriften neu laden',
        unbekanntErklaerung: 'Er wurde entfernt, oder der Link ist nicht mehr gültig.',
      }}
      onErneutAnlegen={() => {
        const letzte = letzteAnlage.current;
        if (letzte.art === 'anschrift') {
          // Steht das Formular zu (abgebrochen), oeffnet "Erneut versuchen" es zuerst.
          if (anlegen.offen) hinzufuegen();
          else anlegen.oeffnen();
          return;
        }
        // Nie das Formular senden, wenn ein Zweck gemeint war. Ist die
        // Anschrift nicht mehr da, zeigt Neuladen den Stand.
        const gruppe = groupAddresses(abschnitt.data?.postalAddresses ?? []).find(
          (eintrag) => eintrag.addressId === letzte.addressId,
        );
        if (gruppe === undefined) {
          abschnitt.reload();
          return;
        }
        // Die offene Auswahl gilt, sonst der zuletzt gesendete Zweck -- dann
        // dieselbe Absendekennung.
        verwenden(gruppe, verwendenFuer === gruppe.addressId && neuerZweck !== '' ? neuerZweck : letzte.usageCode);
      }}
      zusatzmeldung={
        // "Anschrift einfuegen": dieselbe Region, keine zweite (NFR-ACC-001).
        einfuegeMeldung === undefined ? undefined : (
          <MessageBar intent="info">
            <MessageBarBody>{einfuegeMeldung}</MessageBarBody>
          </MessageBar>
        )
      }
    >
      {(data) => (
        <>

          {nurLesen !== undefined ? (
            <MessageBar intent="info">
              <MessageBarBody>
                <strong>{nurLesen.headline}</strong> {nurLesen.explanation}
              </MessageBarBody>
            </MessageBar>
          ) : undefined}

          {ueber?.postalAddress === undefined ? undefined : (
            // P7 A: ein Satz, wenn die Person die Geschäftsanschrift ihrer Organisation übernimmt.
            <Body1 className={ueberStil.ueberOrganisation}>
              Ohne eigene Anschrift gilt die Geschäftsanschrift von{' '}
              <AppLink to={partyHref(ueber.organizationPartyId)}>{ueber.organizationName}</AppLink>. Eine eigene Anschrift hier geht vor.
            </Body1>
          )}

          <Pflegeliste
            id="ad-gueltig"
            titel="Aktuelle Anschriften"
            leer="Für diesen Kontakt ist bisher keine Anschrift hinterlegt."
            anzahl={data.postalAddresses.length}
            hinweis={additionalAddressNotice(data.additionalPostalAddressCount)}
            aktion={nurLesen === undefined ? <AnlegenOeffner
                  steuerung={anlegen}
                  label="Anschrift hinzufügen"
                  disabled={busy}
                  zuruecktreten={
                    absendenFehlgeschlagen ||
                    abschnitt.retryCommand !== undefined ||
                    beendenFuer !== undefined ||
                    verwendenFuer !== undefined ||
                    korrigierenFuer !== undefined
                  }
                /> : undefined}
            vorListe={
              nurLesen === undefined ? (
                <AnlegenFormular
                  steuerung={anlegen}
                  name="Anschrift hinzufügen"
                  className={styles.form}
                  onSenden={hinzufuegen}
                  absenden={
                    // Waehrend "Erneut versuchen" steht, tritt dieser Knopf zurueck -- genau eine Hauptaktion (NFR-UX-004).
                    <Button appearance={absendenFehlgeschlagen ? 'secondary' : 'primary'} type="submit" disabled={busy}>
                      {busy ? 'Wird gespeichert …' : 'Anschrift hinzufügen'}
                    </Button>
                  }
                >
                  {/* Dass LIAISON nicht prueft, einmal und dort, wo man eine Anschrift eingibt. */}
                  <Caption1 className={pflege.hinweis}>{UNCHECKED_EXPLANATION}</Caption1>
                  {/* "Anschrift einfuegen" steht VOR dem Zweck: erst die Anschrift aus
                      der Signatur, dann die Entscheidung, wofuer sie gilt. Kein
                      eigener Knopf -- die Hauptaktion bleibt "Anschrift hinzufuegen". */}
                  <Field label={ADDRESS_PASTE_LABEL} hint={einfuegeHinweis}>
                    <Textarea
                      id="ad-einfuegen"
                      className={pflege.feld}
                      resize="vertical"
                      rows={4}
                      value={einfuegeText}
                      onChange={(_event, geaendert) => fuegeEin(geaendert.value)}
                    />
                  </Field>
                  {einfuegeRest.length > 0 ? (
                    <div className={own.rest}>
                      <Body1 id="ad-einfuegen-rest">
                        <strong>{ADDRESS_PASTE_REST_HEADING}</strong>
                      </Body1>
                      <ul className={own.restliste} aria-labelledby="ad-einfuegen-rest">
                        {einfuegeRest.map((satz, index) => (
                          // Der Index ist der Schluessel: zwei gleiche Zeilen sind moeglich.
                          <li key={index}>
                            <Caption1>{satz}</Caption1>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : undefined}
                  <Field
                    label="Wofür wird diese Anschrift verwendet?"
                    required
                    hint={gewaehlterZweck?.hint ?? null}
                    validationMessage={fehlerFuer('usageCode')}
                  >
                    <RadioGroup value={eingabe.usageCode} onChange={(_event, geaendert) => setze('usageCode')(geaendert.value)}>
                      {OFFERED_USAGES.map((eintrag) => (
                        <Radio key={eintrag.code} value={eintrag.code} label={eintrag.label} />
                      ))}
                    </RadioGroup>
                  </Field>
                  <Field
                    label="Land"
                    required
                    hint={eingabe.countryCode === '' && fehlerFuer('countryCode') === null ? (addressFieldHint(einfuegen, 'countryCode') ?? null) : null}
                    validationMessage={fehlerFuer('countryCode')}
                  >
                    <Select
                      id="ad-land"
                      className={own.land}
                      value={eingabe.countryCode}
                      onChange={(_event, geaendert) => setze('countryCode')(geaendert.value)}
                    >
                      {/* Der Hinweis weicht der Feldmeldung des Servers: zwei Saetze
                          zu demselben Feld waeren eine Rueckmeldung zu viel
                          (Experience-Review 29.09.2026, P3). */}
                      {/* Nur wenn das Einfuegen das Land offen liess -- wer tippt,
                          behaelt die Vorbelegung (Auflage Dirigent -42). */}
                      {eingabe.countryCode === '' ? <option value="">{ADDRESS_CHOOSE_COUNTRY}</option> : undefined}
                      {laender.map((land) => (
                        <option key={land.code} value={land.code}>
                          {land.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field
                    label="Straße und Hausnummer"
                    required
                    hint={eingabe.addressLine1.trim() === '' && fehlerFuer('addressLine1') === null ? (addressFieldHint(einfuegen, 'addressLine1') ?? null) : null}
                    validationMessage={fehlerFuer('addressLine1')}
                  >
                    <Input
                      id="ad-zeile1"
                      className={pflege.feld}
                      autoComplete="address-line1"
                      value={eingabe.addressLine1}
                      onChange={(_event, geaendert) => setze('addressLine1')(geaendert.value)}
                    />
                  </Field>
                  <Field
                    label="Adresszusatz"
                    hint="Zum Beispiel Abteilung, Hinterhaus oder c/o. Kann leer bleiben."
                    validationMessage={fehlerFuer('addressLine2')}
                  >
                    <Input
                      id="ad-zeile2"
                      className={pflege.feld}
                      autoComplete="address-line2"
                      value={eingabe.addressLine2}
                      onChange={(_event, geaendert) => setze('addressLine2')(geaendert.value)}
                    />
                  </Field>
                  {/* Das Pflichtzeichen folgt dem Land (A2): in Deutschland Pflicht,
                      sonst "falls vorhanden" -- in Worten, nicht nur als Sternchen. */}
                  <Field
                    label={plzPflicht ? 'Postleitzahl' : 'Postleitzahl (falls vorhanden)'}
                    required={plzPflicht}
                    validationMessage={fehlerFuer('postalCode')}
                  >
                    <Input
                      id="ad-plz"
                      className={pflege.feld}
                      autoComplete="postal-code"
                      value={eingabe.postalCode}
                      onChange={(_event, geaendert) => setze('postalCode')(geaendert.value)}
                    />
                  </Field>
                  <Field label="Ort" required validationMessage={fehlerFuer('locality')}>
                    <Input
                      id="ad-ort"
                      className={pflege.feld}
                      autoComplete="address-level2"
                      value={eingabe.locality}
                      onChange={(_event, geaendert) => setze('locality')(geaendert.value)}
                    />
                  </Field>
                </AnlegenFormular>
              ) : undefined
            }
          >
            {groupAddresses(data.postalAddresses).map((group) => (
              <Adresse
                key={group.addressId}
                group={group}
                nurLesen={nurLesen !== undefined}
                busy={busy}
                grund={grund}
                onGrund={setGrund}
                beendenFuer={beendenFuer}
                neuerZweck={verwendenFuer === group.addressId ? neuerZweck : ''}
                onNeuerZweck={setNeuerZweck}
                verwendenOffen={verwendenFuer === group.addressId}
                zuruecktreten={absendenFehlgeschlagen}
                onBeendenOeffnen={(address) => {
                  // Ein an einer anderen Zeile gewaehlter Grund gehoert nicht zu dieser.
                  setGrund(OFFERED_ADDRESS_END_REASONS[0]?.code ?? '');
                  verwendenSchliessen();
                  setKorrigierenFuer(undefined);
                  setBeendenFuer(address.addressUsageId);
                }}
                korrektur={
                  <AnschriftKorrigieren
                    name={group.lines.join(', ')}
                    group={group}
                    offen={korrigierenFuer === group.addressId}
                    busy={busy}
                    zuruecktreten={absendenFehlgeschlagen}
                    feldfehler={korrigierenFuer === group.addressId ? feldfehler : []}
                    onOeffnen={() => {
                      setBeendenFuer(undefined);
                      verwendenSchliessen();
                      setKorrigierenFuer(group.addressId);
                    }}
                    onAbbrechen={() => setKorrigierenFuer(undefined)}
                    onSpeichern={(eingabeKorrektur) => korrigieren(group, eingabeKorrektur)}
                  />
                }
                onBeendenAbbrechen={() => setBeendenFuer(undefined)}
                onBeenden={beenden}
                onVerwendenOeffnen={() => {
                  setBeendenFuer(undefined);
                  setKorrigierenFuer(undefined);
                  setNeuerZweck('');
                  setVerwendenFuer(group.addressId);
                }}
                onVerwendenAbbrechen={verwendenSchliessen}
                onVerwenden={() => verwenden(group, neuerZweck)}
              />
            ))}
          </Pflegeliste>

          <Pflegehistorie
            titel="Frühere Anschriften"
            anzahl={data.endedPostalAddresses.length}
            leer="Bisher gibt es keine früheren Anschriften."
            abgeschnitten={data.endedPostalAddressesTruncated ? HISTORY_TRUNCATED_NOTICE : undefined}
          >
            {data.endedPostalAddresses.map((entry) => (
              <FruehereAdresse key={entry.addressUsageId} entry={entry} />
            ))}
          </Pflegehistorie>
        </>
      )}
    </Pflegeabschnitt>
    </>
  );
}
